import { useEffect, useState } from "react";
import { Plus, Pencil, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function TrainingPrograms() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, name, default_hours}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/training-programs");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: "", default_hours: "" });
  const startEdit = (it) => setEditing({ ...it, default_hours: it.default_hours ?? "" });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.name?.trim()) {
      toast.error("Program adı boş olamaz.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        default_hours: editing.default_hours === "" || editing.default_hours == null
          ? null
          : Number(editing.default_hours),
      };
      if (editing.id) {
        await api.put(`/training-programs/${editing.id}`, payload);
        toast.success("Eğitim programı güncellendi.");
      } else {
        await api.post("/training-programs", payload);
        toast.success("Eğitim programı eklendi.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`"${it.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/training-programs/${it.id}`);
      toast.success("Silindi.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Silinemedi.");
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="programs-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <span className="label-eyebrow">/ Eğitim Programları</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
            Program kataloğu
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Sertifika oluştururken "Eğitim Programı" dropdown'unda görünecek listeyi buradan yönetin.
            Her programa varsayılan bir saat değeri de tanımlayabilirsiniz (opsiyonel).
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="program-add-button"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#0B2046] text-white hover:bg-[#113066] font-medium"
        >
          <Plus size={16} weight="bold" /> Yeni Program
        </button>
      </div>

      {editing && (
        <div className="mt-8 max-w-2xl bg-white border border-slate-200 p-6">
          <div className="label-eyebrow mb-4">{editing.id ? "Programı Düzenle" : "Yeni Program"}</div>
          <div className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Program adı *</label>
              <input
                data-testid="program-form-name"
                autoFocus
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="örn. International Cabin Crew Training Program"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Varsayılan saat (opsiyonel)</label>
              <input
                data-testid="program-form-hours"
                type="number"
                step="0.5"
                value={editing.default_hours}
                onChange={(e) => setEditing({ ...editing, default_hours: e.target.value })}
                placeholder="örn. 120"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm font-mono-plex focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
              />
              <p className="text-xs text-slate-500 mt-1">
                Bu program seçildiğinde modüllerin toplam saatleri için referans olarak gösterilir.
                Boş bırakılırsa toplam saat manuel girilen modüllerden hesaplanır.
              </p>
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end gap-2">
            <button onClick={cancel} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2">
              <X size={14} /> Vazgeç
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="program-form-save"
              className="px-5 py-2 bg-[#0B2046] text-white hover:bg-[#113066] font-medium text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <FloppyDisk size={14} weight="bold" /> {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left label-eyebrow">Program Adı</th>
              <th className="px-4 py-3 text-left label-eyebrow">Varsayılan Saat</th>
              <th className="px-4 py-3 text-left label-eyebrow">Eklenme</th>
              <th className="px-4 py-3 text-right label-eyebrow">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-8 text-center label-eyebrow">Yükleniyor…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center">
                  <div className="label-eyebrow">Henüz eğitim programı yok</div>
                  <button onClick={startNew} className="mt-3 text-sm font-medium text-[#0B2046] underline underline-offset-4">
                    İlk programı ekle →
                  </button>
                </td>
              </tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`program-row-${it.id}`}>
                <td className="px-4 py-3">{it.name}</td>
                <td className="px-4 py-3 font-mono-plex">
                  {it.default_hours != null ? `${it.default_hours} saat` : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 font-mono-plex text-xs text-slate-500">
                  {it.created_at ? new Date(it.created_at).toLocaleDateString("tr-TR") : "-"}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => startEdit(it)}
                    data-testid={`program-edit-${it.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                  >
                    <Pencil size={14} /> Düzenle
                  </button>
                  <button
                    onClick={() => remove(it)}
                    data-testid={`program-delete-${it.id}`}
                    className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                  >
                    <Trash size={14} /> Sil
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
